import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { GET_DIGITAL_TWIN_DATA_BY_IDS_QUERY } from "../queries/digitalTwinQueries";
import { GET_INSTANCES_BY_IDS_QUERY } from "../queries/instanceQueries";

/**
 * Hook to check validation status for multiple digital twins
 * Returns an object mapping twin IDs to their validation status
 * Shows DOLLFRONT during loading for performance
 */
export const useMultiTwinValidation = (digitalTwinIds = []) => {
  const [validationStatus, setValidationStatus] = useState({});

  // Fetch digital twins data to get instance IDs
  const { data: twinsData, loading: twinsLoading } = useQuery(
    GET_DIGITAL_TWIN_DATA_BY_IDS_QUERY,
    {
      variables: { ids: digitalTwinIds },
      skip: !digitalTwinIds?.length,
      fetchPolicy: "network-only", // Always fetch fresh data as requested
      onError: error => {},
    }
  );

  // Extract all instance IDs from all twins
  const allInstanceIds = useMemo(() => {
    if (!twinsData?.digitalTwinsByIds) return [];

    const instanceFields = [
      "ct_instance_id",
      "mri_instance_id",
      "coronal_dx_instance_id",
      "sagittal_dx_instance_id",
    ];

    const instanceIds = new Set();

    twinsData.digitalTwinsByIds.forEach(twin => {
      instanceFields.forEach(field => {
        if (twin[field]) {
          instanceIds.add(twin[field]);
        }
      });
    });

    return Array.from(instanceIds);
  }, [twinsData]);

  // Fetch instances data to check validation status
  const { data: instancesData, loading: instancesLoading } = useQuery(
    GET_INSTANCES_BY_IDS_QUERY,
    {
      variables: { ids: allInstanceIds },
      skip: !allInstanceIds.length,
      fetchPolicy: "network-only",
      onError: error => {},
    }
  );

  // Calculate validation status for each twin
  useEffect(() => {
    if (
      twinsLoading ||
      instancesLoading ||
      !twinsData?.digitalTwinsByIds ||
      !instancesData?.instancesByIds
    ) {
      // During loading, set all twins to false (will show DOLLFRONT)
      const loadingStatus = {};
      digitalTwinIds.forEach(twinId => {
        loadingStatus[twinId] = false;
      });
      setValidationStatus(loadingStatus);
      return;
    }

    const newValidationStatus = {};

    twinsData.digitalTwinsByIds.forEach(twin => {
      const twinInstanceIds = [
        twin.ct_instance_id,
        twin.mri_instance_id,
        twin.coronal_dx_instance_id,
        twin.sagittal_dx_instance_id,
      ].filter(Boolean); // Remove null/undefined values

      if (twinInstanceIds.length === 0) {
        // No instances found, consider as not validated
        newValidationStatus[twin._id] = false;
        return;
      }

      // Check if all instances for this twin are validated
      const twinInstances = instancesData.instancesByIds.filter(instance =>
        twinInstanceIds.includes(instance._id)
      );

      const allValidated =
        twinInstances.length > 0 &&
        twinInstances.every(instance => instance.validated === true);

      newValidationStatus[twin._id] = allValidated;
    });

    setValidationStatus(newValidationStatus);
  }, [
    twinsData,
    instancesData,
    twinsLoading,
    instancesLoading,
    digitalTwinIds,
  ]);

  const isLoading = twinsLoading || instancesLoading;

  return {
    validationStatus,
    isLoading,
  };
};
